import Link, { LinkProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import * as React from "react";
import styled, { withTheme } from "styled-components";

export type BreadcrumbsProps = MobiusStyledComponentProps<
    "nav",
    {
        /** CSS string or styled-components function to be injected into this component.
         * @themable */
        css?: StyledProp<BreadcrumbsProps>;
        /** An array of link props */
        links?: LinkProps[];
        /**
         * Indicates how the `css` property is combined with the variant `css` property from the theme.
         * If true, the variant css is applied first and then the component css is applied after causing
         * a merge, much like normal CSS. If false, only the component css is applied, overriding the variant css in the theme.
         */
        mergeCss?: boolean;
        /**
         * Optional props to be passed down to all Link and Typography components.
         * Can be overriden by individual typographyProps in the links array.
         * @themable */
        typographyProps?: TypographyPresentationProps;
    }
>;

export type BreadcrumbsPresentationProps = Omit<BreadcrumbsProps, "links">;

export const Slash = styled(Typography as any).attrs({ children: "/" })`
    margin: 0 10px;
    display: block;
`;

export const StyledSection = styled.section.attrs({ "aria-label": "breadcrumbs" })`
    a {
        background-color: transparent;
        color: #253746;
        transition: color .35s cubic-bezier(.3,.86,.36,.95);
        -webkit-text-decoration: none;
        text-decoration: none;
        &:hover {
            -webkit-text-decoration: none;
            text-decoration: none;
            color: #c8102e;
        }
        span{
            color:unset!important;
        }
    }
    * {
        box-sizing: inherit;
    }
    &:after {
        box-sizing: inherit;
    }
    &:before {
        box-sizing: inherit;
    }
    ul {
        margin: 0 0 22px;
    }
    .as-container {
        &:after {
            clear: both;
            content: "";
            display: block;
        }
        margin: 0 auto;
        max-width: 1180px;
        padding-left: 20px;
        padding-right: 20px;
        width: 100%;
    }
    &.as-breadcrumbs-section {
        padding: 35px 0 10px;
        .as-container {
            max-width: 1310px;
            padding-left: 20px;
            padding-right: 20px;
            margin-left: auto;
            margin-right: auto;
        }
    }
    .as-breadcrumb-list {
        display: flex;
        flex-wrap: wrap;
        font-size: 11px;
        letter-spacing: .22px;
        list-style: none;
        margin: 0;
        padding: 0;
        color: #042359;
        text-transform: capitalize;
        li+li {
            &:before {
                content: "/";
                margin: 0 5px;
            }
        }
        a {
            color: #042359;
            transition: color .3s ease;
            &:hover {
                color: #55a9cb;
            }
            span{
                text-transform: capitalize;
            }
        }
    }
    @media (min-width:768px) {
        &.as-breadcrumbs-section.cat-page-margin{
            margin-top:5%;
        }
        &.as-breadcrumbs-section {
            padding: 51px 0 20px;
        }
        .as-breadcrumb-list {
            font-size: 14px;
            letter-spacing: .28px;
        }        
    }
    @media (max-width:767px) {
        .as-breadcrumb-list {
            font-size: 14px;
            letter-spacing: .22px;
            li+li {
                &:before {
                    margin: 0 5px;
                }
            }
        }
    }

`;

const Breadcrumbs: React.FC<BreadcrumbsProps> = withTheme(({ links, mergeCss, isCatPage, ...otherProps }) => {
    const { spreadProps, applyStyledProp } = applyPropBuilder(otherProps, { component: "breadcrumbs" });
    const typographyProps = spreadProps("typographyProps");
    const resolvedMergeCss = mergeCss ?? otherProps?.theme?.breadcrumbs?.defaultProps?.mergeCss;
    // return null if `links` is an empty empty object, collection, map, or set.
    if (!links || links.length === 0) {
        return null;
    }
    const children = links.map((link: any, index: number) => {
        const children = link.children;
        // yield null if no children
        if (!React.Children.count(children)) {
            return null;
        }
        const key = typeof children === "string" ? `${children}_${index}` : index;
        if (index === links.length - 1) {
            return (
                <li aria-current="page" key={key} data-test-selector={`breadCrumb_${children}`}>{children}</li>
            );
        }
        return (
            <li key={key}>
                <Link typographyProps={typographyProps} {...link} />
            </li>
        );
    });
    // return null if `links` map yields an array of nulls
    if (Object.keys(children.filter((x: typeof Link) => x !== null)).length === 0) {
        return null;
    }

    return (
        <>
            <StyledSection className={isCatPage?"as-breadcrumbs-section cat-page-margin":"as-breadcrumbs-section"}>
                <div className="as-container">
                    <nav aria-label="breadcrumb">
                        <ul className="as-breadcrumb-list">
                            {children}
                        </ul>
                    </nav>
                </div>
            </StyledSection>
        </>
    );
});

Breadcrumbs.displayName = "Breadcrumbs";
/** @component */
export default Breadcrumbs;
